import React, { useState, useEffect } from 'react';
import { UserIcon, ExclamationTriangleIcon, CheckCircleIcon, XCircleIcon, EyeIcon, PencilIcon, PlusIcon, XMarkIcon, ShieldCheckIcon, BeakerIcon, ChevronLeftIcon, ChevronRightIcon, MagnifyingGlassIcon, ClipboardDocumentListIcon, QuestionMarkCircleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { FaDog } from 'react-icons/fa';
import { StatCard, StatGroup } from '../components/StatCard';
import ConfirmationModal from '../components/ConfirmationModal';
import DocumentPreviewModal from '../components/DocumentPreviewModal';
import AddEditDogModal from '../components/AddEditDogModal';
import apiClient from '../utils/apiClient';
import { getDogImageUrl, getMemberImageUrl, shouldUseImageUrls } from '../utils/imageUtils';
import { ModalShell, ModalActions, ModalSection } from '../components/modals/core';
import DogDetailCard from '../components/DogDetailCard';
import GuestBadge from '../components/GuestBadge';
import { isGuestOnly, type Dog } from '../types/dog';


// Create a typed icon component
const DogIcon: React.FC<{ className?: string }> = ({ className }) => {
  const IconComponent = FaDog as React.ComponentType<{ className?: string }>;
  return <IconComponent className={className} />;
};

interface Member {
  member_name: string;
  member_nr: number;
  mitglieder_nr: string;
  email: string;
  foto_path?: string | null;
  foto_path_updated?: string | number | null;
  /** Rollennamen aus `all_dogs_status` (z. B. ['guest']). */
  roles?: string[];
  /** Reiner Gast — vom Backend berechnet. */
  is_guest?: boolean;
  dogs: Dog[];
}

interface ApiResponse {
  success: boolean;
  data: {
    members: Member[];
  };
}

interface ConfirmModalState {
  isOpen: boolean;
  type?: 'success' | 'warning' | 'error' | 'question' | 'info';
  title: string;
  message: string;
  variant?: 'single' | 'two';
  onConfirm?: () => void;
}

const DogManagementBeta: React.FC = () => {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState>({ isOpen: false, title: '', message: '' });
  const [imageErrors, setImageErrors] = useState<{ [key: string]: boolean }>({});
  const [documentModal, setDocumentModal] = useState<{
    isOpen: boolean;
    documents: Array<{ docId: number; filename: string; created_at: string; }>;
    category: 'insurance' | 'tollwut';
    categoryId: number;
    dogId: number;
    dogName: string;
  }>({ isOpen: false, documents: [], category: 'insurance', categoryId: 1, dogId: 0, dogName: '' });
  const [addEditDogModal, setAddEditDogModal] = useState<{
    isOpen: boolean;
    dog?: Dog | null;
    memberId?: number;
    memberName?: string;
  }>({ isOpen: false });
  const [filter, setFilter] = useState<'all' | 'problems' | 'valid'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [dogImageViewer, setDogImageViewer] = useState<{
    isOpen: boolean;
    dogs: Dog[];
    currentIndex: number;
  }>({ isOpen: false, dogs: [], currentIndex: 0 });

  // Calculate optimal grid columns based on dog count - uses full width dynamically
  const getOptimalGridClass = (dogCount: number): string => {
    if (dogCount === 1) {
      return 'grid grid-cols-1 gap-6';
    } else if (dogCount === 2) {
      return 'grid grid-cols-1 md:grid-cols-2 gap-6';
    } else if (dogCount === 3) {
      return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6';
    } else if (dogCount <= 6) {
      return 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-3 gap-6';
    } else {
      return 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-6';
    }
  };

  // Fetch all members and dogs
  const fetchMembersWithDogs = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/members/all_dogs_status');
      
      if (response.data.success) {
        setMembers(response.data.data.members);
        console.log('Members after set:', response.data.data.members);
      } else {
        setConfirmModal({
          isOpen: true,
          type: 'error',
          title: 'Fehler',
          message: 'Mitglieder konnten nicht geladen werden.',
          variant: 'single'
        });
      }
    } catch (error) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: 'Netzwerkfehler beim Laden der Daten.',
        variant: 'single'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembersWithDogs();
  }, []);

  // Aktualisiere documentModal wenn members sich ändern und Modal offen ist
  useEffect(() => {
    if (documentModal.isOpen && members.length > 0) {
      const dog = members.find(member => 
        member.dogs.find(d => d.dog_id === documentModal.dogId)
      )?.dogs.find(d => d.dog_id === documentModal.dogId);
      
      if (dog) {
        const newDocuments = dog.documents[documentModal.category];
        
        setDocumentModal(prev => ({
          ...prev,
          documents: newDocuments
        }));
      }
    }
  }, [members, documentModal.isOpen, documentModal.dogId, documentModal.category]);

  // Keyboard navigation for dog image viewer
  useEffect(() => {
    if (!dogImageViewer.isOpen) return;
    
    const handleKeyPress = (e: KeyboardEvent) => {
      switch(e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          navigateDogImage('prev');
          break;
        case 'ArrowRight':
          e.preventDefault();
          navigateDogImage('next');
          break;
        case 'Escape':
          e.preventDefault();
          closeDogImageViewer();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [dogImageViewer.isOpen]);

  // Calculate statistics
  const stats = {
    totalMembers: members.length,
    totalDogs: members.reduce((sum, member) => sum + member.dogs.length, 0),
    membersWithIssues: members.filter(member => 
      member.dogs.some(dog => hasIssues(dog))
    ).length,
    dogsWithIssues: members.reduce((sum, member) => 
      sum + member.dogs.filter(dog => hasIssues(dog)).length, 0
    )
  };

  // Filter members based on filter state and sort alphabetically by first name
  const filteredMembers = members.filter(member => {
    console.log('Member:', member.member_name, 'mitglieder_nr:', member.mitglieder_nr);
    // Mitglieder ohne Hunde ausblenden
    if (member.dogs.length === 0) return false;

    // Search filter - search in member name and dog names
    if (searchQuery.trim()) {
      const search = searchQuery.toLowerCase().trim();
      const memberNameMatch = member.member_name.toLowerCase().includes(search);
      const dogNameMatch = member.dogs.some(dog =>
        dog.hundename?.toLowerCase().includes(search) ||
        dog.vollstaendiger_name?.toLowerCase().includes(search)
      );
      if (!memberNameMatch && !dogNameMatch) return false;
    }

    if (filter === 'all') return true;
    if (filter === 'problems') return member.dogs.some(dog => hasIssues(dog));
    if (filter === 'valid') return !member.dogs.some(dog => hasIssues(dog));
    return true;
  }).sort((a, b) => {
    // Alphabetische Sortierung nach Vorname (erster Teil des Namens)
    const firstNameA = a.member_name.split(' ')[0].toLowerCase();
    const firstNameB = b.member_name.split(' ')[0].toLowerCase();
    return firstNameA.localeCompare(firstNameB, 'de');
  });

  // Check if dog has issues
  function hasIssues(dog: Dog): boolean {
    const hasInsuranceIssue = !dog.valid_insurance || dog.documents.insurance.length === 0;
    const hasTollwutIssue = !dog.valid_tollwut || dog.documents.tollwut.length === 0;
    const isTollwutExpired = dog.tollwut_end_date ? new Date(dog.tollwut_end_date) < new Date() : false;
    
    return hasInsuranceIssue || hasTollwutIssue || isTollwutExpired;
  }

  // Derive up-to-two-letter initials from the concatenated member name
  // (API liefert nur member_name, kein vorname/name-Split) – analog MemberAvatar.
  function getInitials(fullName: string): string {
    return fullName
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map(part => part.charAt(0).toUpperCase())
      .join('') || '?';
  }

  // Calculate age from birthdate
  function calculateAge(birthdate: string | null | undefined): string {
    if (!birthdate) return 'Unbekannt';
    
    const birth = new Date(birthdate);
    const now = new Date();
    const years = now.getFullYear() - birth.getFullYear();
    const months = now.getMonth() - birth.getMonth();
    
    if (years > 0) {
      const adjustedMonths = months < 0 ? months + 12 : months;
      return `${years} Jahre${adjustedMonths > 0 ? `, ${adjustedMonths} Monate` : ''}`;
    } else {
      const totalMonths = months <= 0 ? 1 : months;
      return `${totalMonths} Monate`;
    }
  }

  // Handle member click to show dog details
  const handleMemberClick = (member: Member) => {
    setSelectedMemberId(member.member_nr);
  };

  // Close member detail view
  const closeMemberDetail = () => {
    setSelectedMemberId(null);
  };

  // Handle insurance validation toggle
  const handleInsuranceValidation = async (dogId: number, currentlyValid: boolean) => {
    const action = currentlyValid ? 'entziehen' : 'genehmigen';
    setConfirmModal({
      isOpen: true,
      type: 'question',
      title: `Versicherung ${action}`,
      message: `Möchten Sie die Gültigkeit der Haftpflichtversicherung wirklich ${action}?`,
      variant: 'two',
      onConfirm: async () => {
        try {
          const endpoint = currentlyValid ? 'unvalidate_insurance' : 'validate_insurance';
          const response = await apiClient.post(`/api/dogs/${endpoint}/${dogId}`);

          if (response.data.success) {
            console.log(`=== VERSICHERUNG ${currentlyValid ? 'ENTZOGEN' : 'GENEHMIGT'} ===`);
            console.log(`Hund ID: ${dogId}`);
            console.log(`Neuer Status: ${!currentlyValid}`);
            console.log('API Response:', response.data);
            
            // DEBUG: Prüfe alle Hunde-IDs vor dem Update
            console.log('DEBUG: Alle Hunde in members:');
            members.forEach(member => {
              console.log(`Mitglied: ${member.member_name}`);
              member.dogs.forEach(dog => {
                console.log(`  - Hund: ${dog.hundename} (ID: ${dog.dog_id}) Versicherung: ${dog.valid_insurance}`);
              });
            });
            console.log(`Suche nach Hund-ID: ${dogId}`);
            
            // Aktualisiere lokalen State SOFORT - EINFACHE VERSION
            setMembers(prevMembers => {
              const newMembers = prevMembers.map(member => ({
                ...member,
                dogs: member.dogs.map(dog => {
                  if (dog.dog_id === dogId) {
                    console.log(`-> GEFUNDEN! Hund "${dog.hundename}" (ID: ${dog.dog_id}) in Mitglied "${member.member_name}"`);
                    console.log(`-> Versicherung Status: ${dog.valid_insurance} -> ${!currentlyValid}`);
                    const updatedDog = {
                      ...dog,
                      valid_insurance: !currentlyValid,
                      insurance_checked_at: response.data.checked_at,
                      insurance_checked_by: response.data.checked_by
                    };
                    console.log(`-> Aktualisierter Hund:`, updatedDog);
                    return updatedDog;
                  }
                  return dog;
                })
              }));
              console.log(`-> Neue Members-State:`, newMembers);
              return newMembers;
            });
            
            // Keine foundDog-Prüfung mehr nötig - das State-Update macht alles
            console.log('✅ Status-Update ausgeführt');
            
            setConfirmModal({
              isOpen: true,
              type: 'success',
              title: 'Erfolgreich',
              message: `Versicherungsstatus wurde erfolgreich ${currentlyValid ? 'entzogen' : 'genehmigt'}.`,
              variant: 'single'
            });
            
            // Force Re-render der DogCards
            setRefreshKey(prev => prev + 1);
            
            // ENTFERNT: setTimeout-Verify war problematisch
            
            console.log('-> DogCard Status aktualisiert + Force Re-render');
            console.log(`=== VERSICHERUNG UPDATE ABGESCHLOSSEN ===`);
            // fetchMembersWithDogs(); // NICHT mehr nötig - lokales Update reicht
          } else {
            throw new Error('API Error');
          }
        } catch (error) {
          setConfirmModal({
            isOpen: true,
            type: 'error',
            title: 'Fehler',
            message: 'Der Versicherungsstatus konnte nicht geändert werden.',
            variant: 'single'
          });
        }
      }
    });
  };

  // Handle tollwut validation toggle
  const handleTollwutValidation = async (dogId: number, currentlyValid: boolean) => {
    const action = currentlyValid ? 'entziehen' : 'genehmigen';
    setConfirmModal({
      isOpen: true,
      type: 'question',
      title: `Tollwutimpfung ${action}`,
      message: `Möchten Sie die Gültigkeit der Tollwutimpfung wirklich ${action}?`,
      variant: 'two',
      onConfirm: async () => {
        try {
          const endpoint = currentlyValid ? 'unvalidate_tollwut' : 'validate_tollwut';
          const response = await apiClient.post(`/api/dogs/${endpoint}/${dogId}`);

          if (response.data.success) {
            console.log(`=== TOLLWUT ${currentlyValid ? 'ENTZOGEN' : 'GENEHMIGT'} ===`);
            console.log(`Hund ID: ${dogId}`);
            console.log(`Neuer Status: ${!currentlyValid}`);
            console.log('API Response:', response.data);
            
            // DEBUG: Prüfe alle Hunde-IDs vor dem Update
            console.log('DEBUG: Alle Hunde in members:');
            members.forEach(member => {
              console.log(`Mitglied: ${member.member_name}`);
              member.dogs.forEach(dog => {
                console.log(`  - Hund: ${dog.hundename} (ID: ${dog.dog_id}) Tollwut: ${dog.valid_tollwut}`);
              });
            });
            console.log(`Suche nach Hund-ID: ${dogId}`);
            
            // Aktualisiere lokalen State SOFORT - EINFACHE VERSION
            setMembers(prevMembers => {
              const newMembers = prevMembers.map(member => ({
                ...member,
                dogs: member.dogs.map(dog => {
                  if (dog.dog_id === dogId) {
                    console.log(`-> GEFUNDEN! Hund "${dog.hundename}" (ID: ${dog.dog_id}) in Mitglied "${member.member_name}"`);
                    console.log(`-> Tollwut Status: ${dog.valid_tollwut} -> ${!currentlyValid}`);
                    const updatedDog = {
                      ...dog,
                      valid_tollwut: !currentlyValid,
                      tollwut_checked_at: response.data.checked_at,
                      tollwut_checked_by: response.data.checked_by
                    };
                    console.log(`-> Aktualisierter Hund:`, updatedDog);
                    return updatedDog;
                  }
                  return dog;
                })
              }));
              console.log(`-> Neue Members-State:`, newMembers);
              return newMembers;
            });
            
            // Keine foundDog-Prüfung mehr nötig - das State-Update macht alles
            console.log('✅ Status-Update ausgeführt');
            
            setConfirmModal({
              isOpen: true,
              type: 'success',
              title: 'Erfolgreich',
              message: `Tollwutstatus wurde erfolgreich ${currentlyValid ? 'entzogen' : 'genehmigt'}.`,
              variant: 'single'
            });
            
            // Force Re-render der DogCards
            setRefreshKey(prev => prev + 1);
            
            // ENTFERNT: setTimeout-Verify war problematisch
            
            console.log('-> DogCard Status aktualisiert + Force Re-render');
            console.log(`=== TOLLWUT UPDATE ABGESCHLOSSEN ===`);
            // fetchMembersWithDogs(); // NICHT mehr nötig - lokales Update reicht
          } else {
            throw new Error('API Error');
          }
        } catch (error) {
          setConfirmModal({
            isOpen: true,
            type: 'error',
            title: 'Fehler',
            message: 'Der Tollwutstatus konnte nicht geändert werden.',
            variant: 'single'
          });
        }
      }
    });
  };

  // Open document preview
  const openDocumentPreview = (dog: Dog, category: 'insurance' | 'tollwut') => {
    setDocumentModal({
      isOpen: true,
      documents: dog.documents[category],
      category: category,
      categoryId: category === 'insurance' ? 2 : 1,
      dogId: dog.dog_id,
      dogName: dog.hundename
    });
  };

  // Close document preview
  const closeDocumentPreview = () => {
    setDocumentModal(prev => ({ ...prev, isOpen: false }));
  };

  // Handle documents change - Hole einzelne Hunddaten vom Server
  const handleDocumentsChange = async () => {
    if (!documentModal.dogId) return;
    
    try {
      console.log(`=== DOKUMENTE GEÄNDERT - HOLE NEUE DATEN ===`);
      console.log(`Hund ID: ${documentModal.dogId}`);
      
      // Hole aktuelle Hunddaten vom Server (neue Route)
      const response = await apiClient.get(`/api/dogs/status/${documentModal.dogId}`);
      
      if (response.data.success && response.data.data) {
        const updatedDog = response.data.data;
        console.log('Neue Hunddaten vom Server:', updatedDog);
        
        // Aktualisiere den Hund in members
        setMembers(prevMembers => 
          prevMembers.map(member => ({
            ...member,
            dogs: member.dogs.map(dog => {
              if (dog.dog_id === documentModal.dogId) {
                console.log(`-> Aktualisiere Hund "${dog.hundename}"`);
                console.log(`-> Neue Dokumente:`, updatedDog.documents);
                return {
                  ...dog,
                  documents: updatedDog.documents,
                  valid_insurance: updatedDog.valid_insurance,
                  valid_tollwut: updatedDog.valid_tollwut,
                  insurance_checked_at: updatedDog.insurance_checked_at,
                  insurance_checked_by: updatedDog.insurance_checked_by,
                  tollwut_checked_at: updatedDog.tollwut_checked_at,
                  tollwut_checked_by: updatedDog.tollwut_checked_by,
                  tollwut_end_date: updatedDog.tollwut_end_date
                };
              }
              return dog;
            })
          }))
        );
        
        // Aktualisiere auch documentModal.documents für die Anzeige
        if (updatedDog.documents[documentModal.category]) {
          setDocumentModal(prev => ({
            ...prev,
            documents: updatedDog.documents[documentModal.category]
          }));
        }
        
        console.log(`=== DOKUMENTE ERFOLGREICH AKTUALISIERT ===`);
      }
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Dokumente:', error);
    }
  };

  // Open add dog modal
  const openAddDogModal = (memberId?: number, memberName?: string) => {
    setAddEditDogModal({
      isOpen: true,
      memberId,
      memberName
    });
  };

  // Open edit dog modal
  const openEditDogModal = (dog: Dog) => {
    setAddEditDogModal({
      isOpen: true,
      dog
    });
  };

  // Close add/edit dog modal
  const closeAddEditDogModal = () => {
    setAddEditDogModal({ isOpen: false });
  };

  // Handle successful dog add/edit
  const handleDogSuccess = () => {
    fetchMembersWithDogs();
    // Force close modal to ensure fresh data is displayed
    setAddEditDogModal({ isOpen: false });
  };

  // Open dog image viewer
  const openDogImageViewer = (dogs: Dog[], currentDog: Dog) => {
    const currentIndex = dogs.findIndex(dog => dog.dog_id === currentDog.dog_id);
    setDogImageViewer({
      isOpen: true,
      dogs,
      currentIndex: currentIndex >= 0 ? currentIndex : 0
    });
  };

  // Close dog image viewer
  const closeDogImageViewer = () => {
    setDogImageViewer({ isOpen: false, dogs: [], currentIndex: 0 });
  };

  // Navigate in dog image viewer
  const navigateDogImage = (direction: 'prev' | 'next') => {
    setDogImageViewer(prev => {
      let newIndex = prev.currentIndex;
      if (direction === 'prev') {
        newIndex = prev.currentIndex > 0 ? prev.currentIndex - 1 : prev.dogs.length - 1;
      } else {
        newIndex = prev.currentIndex < prev.dogs.length - 1 ? prev.currentIndex + 1 : 0;
      }
      return { ...prev, currentIndex: newIndex };
    });
  };

  const renderDogImageViewer = () => {
    if (!dogImageViewer.isOpen || dogImageViewer.dogs.length === 0) return null;

    const currentDog = dogImageViewer.dogs[dogImageViewer.currentIndex];
    if (!currentDog) return null;

    const gender =
      currentDog.geschlecht === 0 || currentDog.geschlecht === 'männlich' || currentDog.geschlecht === 'Männlich'
        ? 'Männlich'
        : currentDog.geschlecht === 1 || currentDog.geschlecht === 'weiblich' || currentDog.geschlecht === 'Weiblich'
          ? 'Weiblich'
          : 'Unbekannt';

    return (
      <ModalShell
        open
        onClose={closeDogImageViewer}
        variant="viewer"
        size="xl"
        mobilePresentation="fullscreen"
        icon={DogIcon}
        title={`Hundebild von ${currentDog.hundename || 'Unbekannt'}`}
        subtitle={`${dogImageViewer.currentIndex + 1} von ${dogImageViewer.dogs.length}`}
        footer={
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-400">
              <span>Rasse: {currentDog.rasse || 'Unbekannt'}</span>
              <span>Alter: {calculateAge(currentDog.geburtstag)}</span>
              <span>Geschlecht: {gender}</span>
            </div>
            <div className="flex items-center justify-end gap-2">
              {dogImageViewer.dogs.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() => navigateDogImage('prev')}
                    aria-label="Vorheriger Hund"
                    className="flex h-11 w-11 items-center justify-center rounded-lg border border-gray-700 bg-gray-800 text-gray-200 transition-colors hover:bg-gray-700"
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => navigateDogImage('next')}
                    aria-label="Nächster Hund"
                    className="flex h-11 w-11 items-center justify-center rounded-lg border border-gray-700 bg-gray-800 text-gray-200 transition-colors hover:bg-gray-700"
                  >
                    <ChevronRightIcon className="h-5 w-5" />
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={() => openEditDogModal(currentDog)}
                className="flex h-11 items-center gap-2 rounded-lg bg-primary-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-primary-700"
              >
                <PencilIcon className="h-4 w-4" />
                Bearbeiten
              </button>
            </div>
          </div>
        }
      >
        <div className="flex min-h-[65dvh] items-center justify-center p-4 sm:min-h-[70vh] sm:p-8">
          {currentDog.image_path && !imageErrors[`viewer-${currentDog.dog_id}`] ? (
            <img
              src={getDogImageUrl(currentDog.dog_id, 'L', currentDog.image_path_updated || undefined)}
              alt={currentDog.hundename}
              className="max-h-[70vh] max-w-full rounded-2xl object-contain shadow-2xl"
              onError={() => setImageErrors(prev => ({ ...prev, [`viewer-${currentDog.dog_id}`]: true }))}
            />
          ) : (
            <div className="rounded-2xl border border-gray-700 bg-gray-800 p-8 text-center">
              <DogIcon className="mx-auto mb-4 h-16 w-16 text-gray-500" />
              <p className="font-medium text-gray-300">
                {currentDog.image_path ? 'Bild konnte nicht geladen werden' : 'Kein Bild verfügbar'}
              </p>
            </div>
          )}
        </div>
      </ModalShell>
    );
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
            <div className="space-y-2 flex-1">
              <div className="h-6 w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
              <div className="h-40 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
              <div className="h-5 w-3/4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              <div className="h-4 w-1/2 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Get selected member from members array
  const selectedMember = members.find(m => m.member_nr === selectedMemberId);
  
  // Show member details view
  if (selectedMember) {
    return (
      <div className="max-w-7xl mx-auto">
        {/* Header with back button */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <DogIcon className="h-8 w-8 text-primary-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Hunde von {selectedMember.member_name}
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Mitglieder-Nr: {selectedMember.mitglieder_nr} • {selectedMember.dogs.length} Hund{selectedMember.dogs.length !== 1 ? 'e' : ''}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={closeMemberDetail}
                className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors shadow-sm text-sm min-w-0 flex-1"
                title="Zurück zur Übersicht"
              >
                <XMarkIcon className="h-5 w-5 flex-shrink-0" />
                <span className="truncate">Zurück</span>
              </button>
              <button
                onClick={() => openAddDogModal(selectedMember.member_nr, selectedMember.member_name)}
                className="flex items-center gap-2 px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors shadow-sm text-sm min-w-0 flex-1"
                title="Neuen Hund hinzufügen"
              >
                <PlusIcon className="h-5 w-5 flex-shrink-0" />
                <span className="truncate">Hund hinzufügen</span>
              </button>
            </div>
          </div>
        </div>

        {/* Dogs Grid - kompakte Karten, max. 2 nebeneinander (fensterbreitenabhängig);
            einzelner Hund nutzt die volle Breite. */}
        <div className={`grid gap-4 grid-cols-1 ${selectedMember.dogs.length > 1 ? 'lg:grid-cols-2' : ''}`}>
          {selectedMember.dogs.map(dog => (
            <DogDetailCard
              key={`${dog.dog_id}-${refreshKey}`}
              dog={dog}
              imageError={!!imageErrors[`dog-${dog.dog_id}`]}
              onImageError={() => setImageErrors(prev => ({ ...prev, [`dog-${dog.dog_id}`]: true }))}
              onOpenImage={() => openDogImageViewer(selectedMember.dogs, dog)}
              onEdit={() => openEditDogModal(dog)}
              onPreviewDocuments={(category) => openDocumentPreview(dog, category)}
              onToggleInsurance={() => handleInsuranceValidation(dog.dog_id, dog.valid_insurance)}
              onToggleTollwut={() => handleTollwutValidation(dog.dog_id, dog.valid_tollwut)}
            />
          ))}
        </div>

        <ConfirmationModal
          visible={confirmModal.isOpen}
          title={confirmModal.title}
          message={confirmModal.message}
          type={confirmModal.type}
          variant={confirmModal.variant}
          button={confirmModal.variant === 'single' ? { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, isOpen: false })) } : undefined}
          buttons={confirmModal.variant === 'two' ? {
            cancel: { text: 'Abbrechen', onPress: () => setConfirmModal(prev => ({ ...prev, isOpen: false })) },
            confirm: { text: 'Bestätigen', onPress: () => { confirmModal.onConfirm?.(); setConfirmModal(prev => ({ ...prev, isOpen: false })); } }
          } : undefined}
        />
        
        <DocumentPreviewModal
          isOpen={documentModal.isOpen}
          onClose={closeDocumentPreview}
          documents={documentModal.documents}
          category={documentModal.category}
          categoryId={documentModal.categoryId}
          dogId={documentModal.dogId}
          dogName={documentModal.dogName}
          onDocumentsChange={handleDocumentsChange}
        />
        
        <AddEditDogModal
          isOpen={addEditDogModal.isOpen}
          onClose={closeAddEditDogModal}
          dog={addEditDogModal.dog}
          memberId={addEditDogModal.memberId}
          memberName={addEditDogModal.memberName}
          onSuccess={handleDogSuccess}
        />

        {renderDogImageViewer()}
      </div>
    );
  }

  // Main overview
  return (
    <div className="max-w-7xl mx-auto">
      {/* Mobile Header */}
      <header className="app-mobile-page-header mb-4 border-b-[3px] border-lime-400 bg-white px-4 py-3 dark:bg-gray-800 sm:hidden">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2.5">
            <DogIcon className="h-6 w-6 shrink-0 text-primary-600 dark:text-primary-400" />
            <div className="min-w-0">
              <h1 className="truncate text-lg font-bold text-gray-900 dark:text-white">Hundeverwaltung</h1>
              <p className="truncate text-xs text-gray-500 dark:text-gray-400">Dokumente und Nachweise prüfen</p>
            </div>
          </div>
          <button
            onClick={() => setShowHelpModal(true)}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gray-100 text-gray-500 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600"
            title="Hilfe"
            aria-label="Hilfe zur Hundeverwaltung"
          >
            <QuestionMarkCircleIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="relative mt-3">
          <MagnifyingGlassIcon className="absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Hund oder Mitglied suchen..."
            className="h-11 w-full rounded-xl border border-gray-200 bg-gray-50 pl-10 pr-3 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
          />
        </div>
      </header>

      {/* Desktop Header */}
      <header className="app-page-header hidden sm:block bg-white dark:bg-gray-800 mb-6 border-b-[3px] border-lime-400">
        {/* Main Header Row */}
        <div className="p-4 md:py-6 md:px-8 flex items-center justify-between gap-4">
          {/* Left: Title */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <DogIcon className="h-7 w-7 text-primary-600 dark:text-primary-400" />
            <div>
              <h1 className="text-[28px] font-bold text-gray-900 dark:text-white">Hundeverwaltung</h1>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Dokumente und Gesundheitsnachweise prüfen</p>
            </div>
          </div>

          {/* Right: Search + Actions */}
          <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
            {/* Desktop Search Field */}
            <div className="relative w-[280px]">
              <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Hund oder Mitglied suchen..."
                className="w-full h-11 pl-11 pr-4 border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 text-sm focus:outline-none focus:border-lime-400 focus:ring-1 focus:ring-lime-400"
              />
            </div>

            <button
              onClick={() => setShowHelpModal(true)}
              className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-xl bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              title="Hilfe"
            >
              <QuestionMarkCircleIcon className="h-5 w-5 text-gray-900 dark:text-white" />
            </button>
          </div>
        </div>
      </header>

      {/* Stats Row - kompakt: alle Kennzahlen in einer Zeile (zentrale StatCard/StatGroup) */}
      {stats.totalDogs > 0 && (
        <div className="mb-4 sm:mb-6">
          <StatGroup columns={3}>
            <StatCard
              icon={DogIcon}
              tone="primary"
              label="Registrierte Hunde"
              value={stats.totalDogs}
              sub={`von ${stats.totalMembers} Mitgliedern`}
            />
            <StatCard
              icon={ExclamationTriangleIcon}
              tone={stats.dogsWithIssues > 0 ? 'error' : 'success'}
              valueTone={stats.dogsWithIssues > 0 ? 'error' : 'success'}
              label="Hunde mit Problemen"
              value={stats.dogsWithIssues}
            />
            <StatCard
              icon={UserIcon}
              tone={stats.membersWithIssues > 0 ? 'error' : 'success'}
              valueTone={stats.membersWithIssues > 0 ? 'error' : 'success'}
              label="Mitglieder betroffen"
              value={stats.membersWithIssues}
            />
          </StatGroup>
        </div>
      )}

      {/* Filter Chips - Design Guide Spec */}
      {stats.totalDogs > 0 && (
      <div className="mb-5 grid grid-cols-3 gap-2 sm:mb-6 sm:flex sm:flex-wrap sm:gap-3">
        <button
          onClick={() => setFilter('all')}
          className={`min-h-10 rounded-xl border px-2 py-2 text-xs font-medium transition-all sm:rounded-full sm:px-4 sm:text-[13px] ${
            filter === 'all'
              ? 'border-[#c9f158] text-[#202020]'
              : 'bg-white dark:bg-gray-800 border-[#e5e7eb] dark:border-gray-700 text-[#202020] dark:text-white hover:border-[#c9f158]'
          }`}
          style={filter === 'all' ? { backgroundColor: '#c9f158' } : {}}
        >
          Alle ({stats.totalMembers})
        </button>
        <button
          onClick={() => setFilter('problems')}
          className={`min-h-10 rounded-xl border px-2 py-2 text-xs font-medium transition-all sm:rounded-full sm:px-4 sm:text-[13px] ${
            filter === 'problems'
              ? 'border-[#c9f158] text-[#202020]'
              : 'bg-white dark:bg-gray-800 border-[#e5e7eb] dark:border-gray-700 text-[#202020] dark:text-white hover:border-[#c9f158]'
          }`}
          style={filter === 'problems' ? { backgroundColor: '#c9f158' } : {}}
        >
          Probleme ({stats.membersWithIssues})
        </button>
        <button
          onClick={() => setFilter('valid')}
          className={`min-h-10 rounded-xl border px-2 py-2 text-xs font-medium transition-all sm:rounded-full sm:px-4 sm:text-[13px] ${
            filter === 'valid'
              ? 'border-[#c9f158] text-[#202020]'
              : 'bg-white dark:bg-gray-800 border-[#e5e7eb] dark:border-gray-700 text-[#202020] dark:text-white hover:border-[#c9f158]'
          }`}
          style={filter === 'valid' ? { backgroundColor: '#c9f158' } : {}}
        >
          Gültig ({stats.totalMembers - stats.membersWithIssues})
        </button>
      </div>
      )}

      {/* Show empty state if no dogs exist */}
      {stats.totalDogs === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-300 dark:border-gray-700 p-6 sm:p-8 md:p-12">
          <div className="max-w-2xl mx-auto text-center">
            <DogIcon className="h-12 w-12 sm:h-16 sm:w-16 text-primary-400 mx-auto mb-3 sm:mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4">
              Willkommen bei der Hundeverwaltung
            </h2>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-6 sm:mb-8 px-2 sm:px-0">
              Es wurden noch keine Hunde von Mitgliedern registriert. Sobald Mitglieder ihre Hunde anlegen,
              erscheinen diese hier zur Überprüfung und Genehmigung der Dokumente.
            </p>
            <div className="bg-primary-50 dark:bg-primary-900/20 rounded-xl p-4 sm:p-6 md:p-8 mb-6 sm:mb-8 text-left border border-primary-200/50 dark:border-primary-700/30">
              <h3 className="text-base font-semibold text-primary-900 dark:text-primary-100 mb-4 sm:mb-6 flex items-center gap-2">
                <ClipboardDocumentListIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                <span className="line-clamp-2">So funktioniert die Hundeverwaltung:</span>
              </h3>
              <div className="space-y-3 sm:space-y-4">
                <div className="flex gap-3 sm:gap-4 bg-white/80 dark:bg-gray-800/50 rounded-lg p-3 sm:p-4 shadow-sm border border-primary-100 dark:border-primary-800/30">
                  <span className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 bg-primary-600 border-2 border-primary-500 shadow-md text-white rounded-full flex items-center justify-center font-bold text-xs sm:text-sm">
                    1
                  </span>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 dark:text-white mb-1 text-sm sm:text-base flex items-center gap-1.5 sm:gap-2">
                      <UserIcon className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600 dark:text-primary-400" />
                      <span className="line-clamp-1">Mitglieder registrieren ihre Hunde</span>
                    </p>
                    <p className="text-gray-700 dark:text-gray-300 text-xs sm:text-sm leading-relaxed">
                      Mitglieder legen ihre Hunde unter "Meine Hunde" an
                      und laden dort die erforderlichen Dokumente (Versicherung und Impfpass) hoch.
                    </p>
                    <p className="text-gray-600 dark:text-gray-400 text-[11px] sm:text-xs mt-1">
                      <strong>Tipp:</strong> Bei Mitgliedern ohne Internetzugang können Sie als Vorstand stellvertretend Hunde anlegen.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3 sm:gap-4 bg-white/80 dark:bg-gray-800/50 rounded-lg p-3 sm:p-4 shadow-sm border border-primary-100 dark:border-primary-800/30">
                  <span className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 bg-primary-600 border-2 border-primary-500 shadow-md text-white rounded-full flex items-center justify-center font-bold text-xs sm:text-sm">
                    2
                  </span>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 dark:text-white mb-1 text-sm sm:text-base flex items-center gap-1.5 sm:gap-2">
                      <EyeIcon className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600 dark:text-primary-400" />
                      <span className="line-clamp-1">Dokumente werden sichtbar</span>
                    </p>
                    <div className="space-y-2">
                      <p className="text-gray-700 dark:text-gray-300 text-xs sm:text-sm">
                        Sobald Mitglieder Hunde anlegen, erscheinen diese automatisch hier in der Übersicht.
                      </p>
                      <div className="bg-info-50 dark:bg-info-900/20 rounded-md p-2 border border-info-200 dark:border-info-700/30">
                        <ul className="text-[11px] sm:text-xs text-info-700 dark:text-info-300 space-y-0.5 sm:space-y-1">
                          <li>• Rote Markierung bei fehlenden Dokumenten</li>
                          <li>• Orange bei ablaufender Tollwutimpfung</li>
                          <li>• Grün bei vollständigen Dokumenten</li>
                        </ul>
                      </div>
                      <p className="text-[11px] sm:text-xs text-gray-600 dark:text-gray-400">
                        Filter nutzen: "Probleme" zeigt nur Hunde mit fehlenden/ungültigen Dokumenten.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 sm:gap-4 bg-white/80 dark:bg-gray-800/50 rounded-lg p-3 sm:p-4 shadow-sm border border-primary-100 dark:border-primary-800/30">
                  <span className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 bg-primary-600 border-2 border-primary-500 shadow-md text-white rounded-full flex items-center justify-center font-bold text-xs sm:text-sm">
                    3
                  </span>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 dark:text-white mb-1 text-sm sm:text-base flex items-center gap-1.5 sm:gap-2">
                      <ShieldCheckIcon className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600 dark:text-primary-400" />
                      <span className="line-clamp-1">Dokumente prüfen & genehmigen</span>
                    </p>
                    <div className="space-y-2">
                      <p className="text-gray-700 dark:text-gray-300 text-xs sm:text-sm">
                        Prüfen Sie hochgeladene Versicherungs- und Impfdokumente.
                      </p>
                      <div className="bg-success-50 dark:bg-success-900/20 rounded-md p-2 border border-success-200 dark:border-success-700/30">
                        <ul className="text-[11px] sm:text-xs text-success-700 dark:text-success-300 space-y-0.5 sm:space-y-1">
                          <li>• Klick auf "X Dokumente" zum Ansehen</li>
                          <li>• Grüner Haken = Genehmigen</li>
                          <li>• Rotes X = Ablehnen</li>
                          <li>• Genehmigung kann jederzeit entzogen werden</li>
                        </ul>
                      </div>
                      <p className="text-[11px] sm:text-xs text-gray-600 dark:text-gray-400">
                        <strong>Hinweis:</strong> Bei fehlenden oder ungenehmigten Dokumenten werden Mitglieder und Trainer auf Probleme hingewiesen.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 sm:gap-4 bg-white/80 dark:bg-gray-800/50 rounded-lg p-3 sm:p-4 shadow-sm border border-primary-100 dark:border-primary-800/30">
                  <span className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 bg-primary-600 border-2 border-primary-500 shadow-md text-white rounded-full flex items-center justify-center font-bold text-xs sm:text-sm">
                    4
                  </span>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 dark:text-white mb-1 text-sm sm:text-base flex items-center gap-1.5 sm:gap-2">
                      <BeakerIcon className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600 dark:text-primary-400" />
                      <span className="line-clamp-1">Ablaufdaten überwachen</span>
                    </p>
                    <p className="text-gray-700 dark:text-gray-300 text-xs sm:text-sm leading-relaxed mb-2">
                      Das System überwacht automatisch die Gültigkeit der Tollwutimpfungen.
                    </p>
                    <div className="bg-warning-50 dark:bg-warning-900/20 rounded-md p-2 border border-warning-200 dark:border-warning-700/30">
                      <ul className="text-[11px] sm:text-xs text-warning-700 dark:text-warning-300 space-y-0.5 sm:space-y-1">
                        <li>• <strong>30 Tage vor Ablauf:</strong> Orange Warnung</li>
                        <li>• <strong>Nach Ablauf:</strong> Automatisch ungültig</li>
                        <li>• <strong>Benachrichtigung:</strong> Mitglied wird informiert</li>
                        <li>• <strong>Statistik:</strong> Zähler "Probleme" erhöht sich</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 bg-info-50 dark:bg-info-900/20 border border-primary-200 dark:border-primary-700/50 rounded-lg">
                <h4 className="font-semibold text-primary-900 dark:text-primary-100 mb-2 flex items-center gap-2">
                  <UserIcon className="h-5 w-5" />
                  Hilfe für Mitglieder ohne Internetzugang
                </h4>
                <p className="text-sm text-primary-700 dark:text-primary-300">
                  Als Vorstand können Sie für Mitglieder ohne Internetzugang stellvertretend Hunde anlegen
                  und Dokumente hochladen. Nutzen Sie dazu den Button "Hund hinzufügen" oben rechts.
                </p>
              </div>
            </div>
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg shadow-sm font-medium">
              <EyeIcon className="h-5 w-5" />
              Warte auf erste Hunderegistrierungen...
            </div>
          </div>
        </div>
      ) : (
        /* Members Grid – dichter (bis 4 Karten/Reihe, kompakter) */
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 sm:gap-3 lg:grid-cols-3 xl:grid-cols-4">
          {filteredMembers.map(member => {
            const problemCount = member.dogs.filter(dog => hasIssues(dog)).length;
            const allDogsValid = member.dogs.length > 0 && member.dogs.every(dog => !hasIssues(dog));
            // Gäste werden hervorgehoben: eigener Rahmen-Akzent + Badge am Namen.
            const isGuest = member.is_guest ?? isGuestOnly(member.roles);

            return (
            <div
              key={member.member_nr}
              onClick={() => handleMemberClick(member)}
              className={`group flex cursor-pointer flex-col gap-2.5 rounded-xl border bg-white p-3 shadow-sm transition-all active:scale-[0.99] hover:shadow-md dark:bg-[#1c2432] ${
                isGuest
                  ? 'border-info-300 hover:border-info-500 dark:border-info-800/70 dark:hover:border-info-600'
                  : 'border-gray-200 hover:border-primary-400 dark:border-gray-700 dark:hover:border-primary-600'
              }`}
            >
              {/* Header: Avatar + Info */}
              <div className="flex items-center gap-3">
                <div className="relative flex-shrink-0">
                  {member.foto_path && !imageErrors[`member-${member.member_nr}`] ? (
                    <img
                      src={getMemberImageUrl(member.member_nr, 'S', member.foto_path_updated || undefined)}
                      alt={member.member_name}
                      className="h-12 w-12 rounded-full object-cover ring-2 ring-gray-200 transition-all group-hover:ring-primary-400 dark:ring-gray-700 dark:group-hover:ring-primary-600"
                      onError={() => setImageErrors(prev => ({ ...prev, [`member-${member.member_nr}`]: true }))}
                    />
                  ) : (
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 ring-2 ring-primary-200 dark:bg-primary-900/40 dark:ring-primary-800">
                      <span className="text-sm font-bold text-primary-700 dark:text-primary-300">{getInitials(member.member_name)}</span>
                    </div>
                  )}

                  {/* Issue Badge */}
                  {problemCount > 0 && (
                    <div className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-error-500 shadow ring-2 ring-white dark:ring-gray-800">
                      <span className="text-[10px] font-bold text-white">{problemCount}</span>
                    </div>
                  )}

                  {/* All Valid Badge */}
                  {allDogsValid && (
                    <div className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-success-500 shadow ring-2 ring-white dark:ring-gray-800">
                      <CheckCircleIcon className="h-3 w-3 text-white" />
                    </div>
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <h3 className="truncate text-sm font-bold text-gray-900 dark:text-white">
                      {member.member_name}
                    </h3>
                    {isGuest && <GuestBadge />}
                  </div>
                  {member.mitglieder_nr ? (
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      Nr. {member.mitglieder_nr}
                    </p>
                  ) : (
                    <p className="text-xs font-medium italic text-gray-400 dark:text-gray-500">
                      Keine Mitgliedsnummer
                    </p>
                  )}

                  {/* Status Badge Inline */}
                  <div className="mt-1.5">
                    {problemCount > 0 ? (
                      <span className="inline-flex items-center gap-1 rounded border border-error-200 bg-error-50 px-1.5 py-0.5 text-[10px] font-semibold text-error-700 dark:border-error-800/60 dark:bg-error-900/30 dark:text-error-300">
                        <ExclamationTriangleIcon className="h-3 w-3" />
                        {problemCount} Problem{problemCount !== 1 ? 'e' : ''}
                      </span>
                    ) : allDogsValid ? (
                      <span className="inline-flex items-center gap-1 rounded border border-success-200 bg-success-50 px-1.5 py-0.5 text-[10px] font-semibold text-success-700 dark:border-success-800/60 dark:bg-success-900/30 dark:text-success-300">
                        <CheckCircleIcon className="h-3 w-3" />
                        Alle gültig
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded border border-gray-300 bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-600 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300">
                        <InformationCircleIcon className="h-3 w-3" />
                        Keine Docs
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Dogs Preview & Count */}
              <div className="mt-auto flex items-center justify-between border-t border-gray-200 pt-2.5 dark:border-gray-700">
                <div className="flex items-center gap-1.5">
                  <DogIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                    {member.dogs.length} {member.dogs.length !== 1 ? 'Hunde' : 'Hund'}
                  </span>
                </div>

                {/* Dog Images Preview + Hover Arrow */}
                <div className="flex items-center gap-1.5">
                  <div className="flex -space-x-2">
                    {member.dogs.slice(0, 3).map((dog) => (
                      <div
                        key={`${dog.dog_id}-${refreshKey}`}
                        className="relative group/dog"
                        title={dog.hundename}
                      >
                        {dog.image_path && !imageErrors[`dog-small-${dog.dog_id}`] ? (
                          <button
                            type="button"
                            aria-label={`Bild von ${dog.hundename} vergrößern`}
                            onClick={(event) => {
                              event.stopPropagation();
                              openDogImageViewer(member.dogs, dog);
                            }}
                            className="block rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                          >
                            <img
                              src={getDogImageUrl(dog.dog_id, 'S', dog.image_path_updated || undefined)}
                              alt={dog.hundename}
                              className="h-7 w-7 rounded-full border-2 border-white object-cover transition-transform group-hover/dog:scale-110 dark:border-gray-800"
                              onError={() => setImageErrors(prev => ({ ...prev, [`dog-small-${dog.dog_id}`]: true }))}
                            />
                          </button>
                        ) : (
                          <div className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-primary-600 transition-transform group-hover/dog:scale-110 dark:border-gray-800">
                            <DogIcon className="h-3.5 w-3.5 text-white" />
                          </div>
                        )}
                        {hasIssues(dog) && (
                          <div className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border border-white bg-error-500 dark:border-gray-800"></div>
                        )}
                      </div>
                    ))}
                    {member.dogs.length > 3 && (
                      <div className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-gray-200 dark:border-gray-800 dark:bg-gray-700">
                        <span className="text-[10px] font-bold text-gray-600 dark:text-gray-300">+{member.dogs.length - 3}</span>
                      </div>
                    )}
                  </div>
                  <ChevronRightIcon className="h-4 w-4 text-gray-300 transition-colors group-hover:text-primary-500 dark:text-gray-600 dark:group-hover:text-primary-400" />
                </div>
              </div>
            </div>
          )})}
        </div>
      )}

      <ConfirmationModal
        visible={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        variant={confirmModal.variant}
        button={confirmModal.variant === 'single' ? { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, isOpen: false })) } : undefined}
        buttons={confirmModal.variant === 'two' ? {
          cancel: { text: 'Abbrechen', onPress: () => setConfirmModal(prev => ({ ...prev, isOpen: false })) },
          confirm: { text: 'Bestätigen', onPress: () => { confirmModal.onConfirm?.(); setConfirmModal(prev => ({ ...prev, isOpen: false })); } }
        } : undefined}
      />
      
      <DocumentPreviewModal
        isOpen={documentModal.isOpen}
        onClose={closeDocumentPreview}
        documents={documentModal.documents}
        category={documentModal.category}
        categoryId={documentModal.categoryId}
        dogId={documentModal.dogId}
        dogName={documentModal.dogName}
        onDocumentsChange={handleDocumentsChange}
      />
      
      <AddEditDogModal
        isOpen={addEditDogModal.isOpen}
        onClose={closeAddEditDogModal}
        dog={addEditDogModal.dog}
        memberId={addEditDogModal.memberId}
        memberName={addEditDogModal.memberName}
        onSuccess={handleDogSuccess}
      />

      {renderDogImageViewer()}

      {/* Help Modal */}
      <ModalShell
        open={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        variant="detail"
        size="lg"
        title="Hilfe - Hundeverwaltung"
        icon={QuestionMarkCircleIcon}
        accent="brand"
        footer={
          <ModalActions
            primary={{ label: 'Verstanden', onClick: () => setShowHelpModal(false) }}
          />
        }
      >
          <ModalSection className="space-y-6">
              {/* Überblick */}
              <div className="bg-primary-50 dark:bg-primary-900/20 rounded-xl p-6 border border-primary-200/50 dark:border-primary-700/30">
                <h4 className="text-lg font-semibold text-primary-900 dark:text-primary-100 mb-3 flex items-center gap-2">
                  <InformationCircleIcon className="h-6 w-6" />
                  Überblick
                </h4>
                <p className="text-sm text-primary-700 dark:text-primary-300 leading-relaxed">
                  Die Hundeverwaltung dient zur Erfassung und Überprüfung aller Hunde der Vereinsmitglieder.
                  Hier werden die erforderlichen Dokumente (Haftpflichtversicherung und Tollwutimpfung) hochgeladen
                  und vom Vorstand geprüft. Bei fehlenden Dokumenten werden Mitglieder und Trainer informiert.
                </p>
              </div>

              {/* Pflichtdokumente */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <ClipboardDocumentListIcon className="h-6 w-6 text-primary-600" />
                  Erforderliche Dokumente
                </h4>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="bg-info-50 dark:bg-info-900/20 rounded-lg p-4 border border-info-200 dark:border-info-700/50">
                    <div className="flex items-center gap-2 mb-2">
                      <ShieldCheckIcon className="h-5 w-5 text-info-600 dark:text-info-400" />
                      <h5 className="font-semibold text-info-900 dark:text-info-100">Haftpflichtversicherung</h5>
                    </div>
                    <ul className="text-sm text-info-700 dark:text-info-300 space-y-1">
                      <li>• Gültiger Versicherungsnachweis erforderlich</li>
                      <li>• Upload als PDF oder Bilddatei</li>
                      <li>• Muss vom Vorstand genehmigt werden</li>
                      <li>• Grün = Genehmigt, Rot = Nicht genehmigt</li>
                    </ul>
                  </div>

                  <div className="bg-success-50 dark:bg-success-900/20 rounded-lg p-4 border border-success-200 dark:border-success-700/50">
                    <div className="flex items-center gap-2 mb-2">
                      <BeakerIcon className="h-5 w-5 text-success-600 dark:text-success-400" />
                      <h5 className="font-semibold text-success-900 dark:text-success-100">Tollwutimpfung</h5>
                    </div>
                    <ul className="text-sm text-success-700 dark:text-success-300 space-y-1">
                      <li>• Gültiger Impfpass mit Tollwutimpfung</li>
                      <li>• Ablaufdatum wird überwacht</li>
                      <li>• Warnung bei Ablauf in &lt; 30 Tagen</li>
                      <li>• Automatisch ungültig nach Ablauf</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Berechtigungen */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <UserIcon className="h-6 w-6 text-primary-600" />
                  Berechtigungen
                </h4>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 border-2 border-gray-300 dark:border-gray-700">
                    <h5 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Mitglieder</h5>
                    <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                      <li>• Eigene Hunde anlegen und bearbeiten</li>
                      <li>• Dokumente hochladen</li>
                      <li>• Genehmigungsstatus einsehen</li>
                      <li>• Nur Zugriff auf eigene Daten</li>
                    </ul>
                  </div>

                  <div className="bg-warning-50 dark:bg-warning-900/20 rounded-lg p-4 border border-warning-200 dark:border-warning-700/50">
                    <h5 className="font-semibold text-warning-900 dark:text-warning-100 mb-2">Vorstand</h5>
                    <ul className="text-sm text-warning-700 dark:text-warning-300 space-y-1">
                      <li>• Hunde für ALLE Mitglieder anlegen</li>
                      <li>• Alle Dokumente einsehen</li>
                      <li>• Dokumente genehmigen/ablehnen</li>
                      <li>• Genehmigungen entziehen</li>
                      <li>• Erweiterte Informationen (Prüfer, Datum)</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Arbeitsablauf */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Arbeitsablauf für Vorstand
                </h4>

                <div className="space-y-3">
                  <div className="flex gap-4 bg-white dark:bg-gray-900/50 rounded-lg p-4 shadow-md border border-gray-300 dark:border-gray-700">
                    <span className="flex-shrink-0 w-8 h-8 bg-primary-600 border-2 border-primary-500 shadow-md text-white rounded-full flex items-center justify-center font-bold text-sm">
                      1
                    </span>
                    <div>
                      <h5 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Mitglied auswählen</h5>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Klicken Sie auf die Mitgliederkarte in der Übersicht, um die Hunde zu sehen.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4 bg-white dark:bg-gray-900/50 rounded-lg p-4 shadow-md border border-gray-300 dark:border-gray-700">
                    <span className="flex-shrink-0 w-8 h-8 bg-primary-600 border-2 border-primary-500 shadow-md text-white rounded-full flex items-center justify-center font-bold text-sm">
                      2
                    </span>
                    <div>
                      <h5 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Dokumente prüfen</h5>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Klicken Sie auf "X Dokumente" bei den Status-Karten, um die hochgeladenen Dokumente einzusehen.
                        Nutzen Sie Zoom und Navigation im Dokument-Viewer.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4 bg-white dark:bg-gray-900/50 rounded-lg p-4 shadow-md border border-gray-300 dark:border-gray-700">
                    <span className="flex-shrink-0 w-8 h-8 bg-primary-600 border-2 border-primary-500 shadow-md text-white rounded-full flex items-center justify-center font-bold text-sm">
                      3
                    </span>
                    <div>
                      <h5 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Genehmigung erteilen</h5>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Nach erfolgreicher Prüfung: Grünen Haken klicken für Genehmigung.
                        Bei Problemen: Rotes X für Ablehnung. Genehmigungen können jederzeit entzogen werden.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4 bg-white dark:bg-gray-900/50 rounded-lg p-4 shadow-md border border-gray-300 dark:border-gray-700">
                    <span className="flex-shrink-0 w-8 h-8 bg-primary-600 border-2 border-primary-500 shadow-md text-white rounded-full flex items-center justify-center font-bold text-sm">
                      4
                    </span>
                    <div>
                      <h5 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Hund hinzufügen (optional)</h5>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Für Mitglieder ohne Internetzugang: Klicken Sie auf "Hund hinzufügen",
                        wählen Sie das Mitglied aus und erfassen Sie die Hundedaten.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Problemerkennung */}
              <div className="bg-error-50 dark:bg-error-900/20 rounded-xl p-4 border border-error-200 dark:border-error-700/50">
                <div className="flex items-start gap-3">
                  <ExclamationTriangleIcon className="h-6 w-6 text-error-600 dark:text-error-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h5 className="font-semibold text-error-900 dark:text-error-100 mb-2">
                      Automatische Problemerkennung
                    </h5>
                    <ul className="text-sm text-error-700 dark:text-error-300 space-y-1">
                      <li>• <strong>Rote Umrandung:</strong> Hunde mit fehlenden/ungültigen Dokumenten</li>
                      <li>• <strong>Orange Warnung:</strong> Tollwutimpfung läuft bald ab (&lt;30 Tage)</li>
                      <li>• <strong>Statistik "Probleme":</strong> Anzahl der Mitglieder/Hunde mit offenen Anforderungen</li>
                      <li>• <strong>Filter "Probleme":</strong> Zeigt nur Mitglieder mit ungültigen Dokumenten</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Wichtige Hinweise */}
              <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 border border-amber-200 dark:border-amber-700/50">
                <div className="flex items-start gap-3">
                  <InformationCircleIcon className="h-6 w-6 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h5 className="font-semibold text-amber-900 dark:text-amber-100 mb-2">
                      Wichtige Hinweise
                    </h5>
                    <ul className="text-sm text-amber-700 dark:text-amber-300 space-y-1">
                      <li>• Ohne genehmigte Dokumente ist <strong>keine Trainingsteilnahme</strong> möglich</li>
                      <li>• Dokumente sollten <strong>rechtzeitig vor Ablauf</strong> erneuert werden</li>
                      <li>• Alle Genehmigungen werden mit <strong>Prüfer und Datum</strong> dokumentiert</li>
                      <li>• Gelöschte Dokumente können <strong>nicht wiederhergestellt</strong> werden</li>
                      <li>• Bei Fragen zur Genehmigung: <strong>Vorstand kontaktieren</strong></li>
                    </ul>
                  </div>
                </div>
              </div>
          </ModalSection>
      </ModalShell>
    </div>
  );
};

export default DogManagementBeta;
